import type { MouseEvent, ReactElement, ReactNode } from 'react';
import { Box, Button, Divider, Drawer, Group, Stack, Text, UnstyledButton } from '@mantine/core';
import { Children, cloneElement, Fragment, isValidElement, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { IconFigmaMoreHorizontal } from '@/assets/icons';

import { filterSheetStyles } from './MobileListFilters';

interface ActionButtonProps {
  variant?: string;
  color?: string;
  fullWidth?: boolean;
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
}

type ActionElement = ReactElement<ActionButtonProps>;

const flattenActions = (children: ReactNode): ActionElement[] =>
  Children.toArray(children).flatMap((child) => {
    if (!isValidElement<{ children?: ReactNode }>(child)) return [];
    if (child.type === Fragment) return flattenActions(child.props.children);
    return [child as ActionElement];
  });

const isDanger = (element: ActionElement) => element.props.color === 'red';
const isFilled = (element: ActionElement) =>
  !element.props.variant || element.props.variant === 'filled';

export const MobileActionBar = ({ children }: { children: ReactNode }) => {
  const { t } = useTranslation();
  const [opened, setOpened] = useState(false);

  const actions = flattenActions(children);
  if (actions.length === 0) return null;

  const primary =
    actions.find((action) => isFilled(action) && !isDanger(action)) ??
    actions.find((action) => !isDanger(action)) ??
    actions[0];
  const rest = actions.filter((action) => action !== primary);
  const secondary = rest.filter((action) => !isDanger(action));
  const danger = rest.filter(isDanger);

  const inSheet = (action: ActionElement) =>
    cloneElement(action, {
      fullWidth: true,
      onClick: (event: MouseEvent<HTMLButtonElement>) => {
        setOpened(false);
        action.props.onClick?.(event);
      }
    });

  return (
    <>
      <span className="mobile-action-bar-anchor" hidden />
      {createPortal(
        <Box
          hiddenFrom="sm"
          className="mobile-action-bar"
          style={{
            position: 'fixed',
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 90,
            padding: '12px 16px calc(12px + env(safe-area-inset-bottom))',
            backgroundColor: 'var(--color-bg-surface)',
            borderTop: '1px solid var(--color-border-soft)',
            transform: 'translateZ(0)'
          }}
        >
          <Group gap={8} wrap="nowrap">
            <Box style={{ flex: 1, minWidth: 0 }}>
              {/* Glavna akcija u traci je uvijek "puna" (zlatna), i kad je na webu outline. */}
              {cloneElement(primary, {
                fullWidth: true,
                ...(!isFilled(primary) && !isDanger(primary) && { variant: 'filled' })
              })}
            </Box>
            {rest.length > 0 && (
              <UnstyledButton
                onClick={() => setOpened(true)}
                aria-label={t('common.moreActions')}
                style={{
                  width: 44,
                  height: 44,
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 6,
                  border: '1px solid var(--color-border-default)',
                  backgroundColor: 'var(--color-bg-surface)',
                  color: 'var(--color-text-primary)'
                }}
              >
                <IconFigmaMoreHorizontal size={18} />
              </UnstyledButton>
            )}
          </Group>
        </Box>,
        document.body
      )}

      <Drawer
        opened={opened}
        onClose={() => setOpened(false)}
        position="bottom"
        title={
          <Text fz={15} fw={600} style={{ color: 'var(--color-text-primary)' }}>
            {t('common.moreActions')}
          </Text>
        }
        styles={filterSheetStyles}
      >
        <Stack gap={8}>
          {secondary.map(inSheet)}
          {danger.length > 0 && secondary.length > 0 && (
            <Divider my={4} color="var(--color-border-soft)" />
          )}
          {danger.map(inSheet)}
          <Button variant="default" fullWidth mt={4} onClick={() => setOpened(false)}>
            {t('common.cancel')}
          </Button>
        </Stack>
      </Drawer>
    </>
  );
};
